import { observable, action, toJS } from 'mobx';
import * as API from '../lib/api';

const debug = require('debug')('Oracle\'s Elixir Valorant Store');

class mainStore {
  currentRoute = observable.box();

  setCurrentRoute = action((route) => {
    this.currentRoute.set(route);
  });

  /**
   * User authentication
   */
  userLoginStatusChecked = observable.box(false);
  userLoggingIn = observable.box(false);
  loginError = observable.box(false);
  userInfoFetching = observable.box(false);
  userLoggedIn = observable.box(false);
  userInfo = observable.object({});
  userGroupMemberships = observable.array([]);

  userIsSettingNewPassword = observable.box(false);
  userObjectFromAuthResponse = observable.box();
  userIsResettingPassword = observable.box(false);

  setUserLoginStatusChecked = action((status) => {
    this.userLoginStatusChecked.set(status);
  })

  setUserLoggingIn = action((status) => {
    this.userLoggingIn.set(status);
  })

  setLoginError = action((message) => {
    this.loginError.set(message);
  })

  setUserInfoFetching = action((state) => {
    this.userInfoFetching.set(state);
  })

  setUserIsSettingNewPassword = action((state) => {
    this.userIsSettingNewPassword.set(state);
  })

  setUserIsResettingPassword = action((state) => {
    this.userIsResettingPassword.set(state);
  })

  setUserObjectFromAuthResponse = action((userObject) => {
    this.userObjectFromAuthResponse.set(userObject);
  })

  setUserLoggedIn = action((sub) => {
    this.userLoggedIn.set(sub);
  })

  /**
   * Player search
   */
  searchingForPlayers = observable.box(false);
  playerSearchResults = observable.array([]);

  setSearchingForPlayers = action((state) => {
    this.searchingForPlayers.set(state);
  })

  /**
   * Individual players
   */
  playerProfileDataMap = observable.map({});
  playerProfileFetching = observable.box(false);
  playerStatsFetching = observable.box(false);
  playerMatchHistoryFetching = observable.box(false);
  playerProfileInView = observable.box(null);

  setPlayerProfileFetching = action((isFetching) => {
    this.playerProfileFetching.set(isFetching);
  })

  setPlayerStatsFetching = action((isFetching) => {
    this.playerStatsFetching.set(isFetching);
  })

  setPlayerMatchHistoryFetching = action((isFetching) => {
    this.playerMatchHistoryFetching.set(isFetching);
  })

  addPlayerProfileData = action((playerId, data) => {
    this.playerProfileDataMap[playerId] = data;
  })

  updatePlayerProfileData = action((playerId, data) => {
    const existingMatchHistory = this.playerProfileDataMap[playerId].matchHistory;
    const existingStatsBySeries = this.playerProfileDataMap[playerId].statsBySeries;
    const existingStatsBySplit = this.playerProfileDataMap[playerId].statsBySplit;
    const existingAgentStats = this.playerProfileDataMap[playerId].agentStats;
    this.playerProfileDataMap[playerId] = data;
    this.playerProfileDataMap[playerId].matchHistory = existingMatchHistory;
    this.playerProfileDataMap[playerId].statsBySeries = existingStatsBySeries;
    this.playerProfileDataMap[playerId].statsBySplit = existingStatsBySplit;
    this.playerProfileDataMap[playerId].agentStats = existingAgentStats;
  })

  addPlayerNotes = action((playerId, data) => {
    this.playerProfileDataMap[playerId].notes = data;
  })

  addPlayerStatsBySeriesData = action((playerId, data) => {
    this.playerProfileDataMap[playerId].statsBySeries = data;
  })

  addPlayerStatsBySplitData = action((playerId, data) => {
    this.playerProfileDataMap[playerId].statsBySplit = data;
  })

  addPlayerMatchHistoryData = action((playerId, data) => {
    this.playerProfileDataMap[playerId].matchHistory = data;
  })

  constructAgentStats = action((playerId, sinceDate) => {
    const agentStatsMap = {};
    for (let game of this.playerProfileDataMap[playerId].matchHistory) {
      if (new Date(game.seriesDate) < sinceDate) {
        continue;
      }

      if (!agentStatsMap[game.agentId]) {
        agentStatsMap[game.agentId] = {
          agentId: game.agentId,
          agentName: game.agentName,
          gameWins: 0,
          gameLosses: 0,
          roundWins: 0,
          roundLosses: 0,
          totalRounds: 0,
          totalRAR: 0,
          totalKastPlusMinus: 0,
          totalFk: 0,
          totalUfd: 0
        };
      }

      if (agentStatsMap[game.agentId].roundWins > agentStatsMap[game.agentId].roundLosses) {
        agentStatsMap[game.agentId].gameWins += 1;
      } else {
        agentStatsMap[game.agentId].gameLosses += 1;
      }

      const rarValueToUse = game.roundsAboveReplacement !== null ? game.roundsAboveReplacement : game.agnosticRar;

      agentStatsMap[game.agentId].roundWins += game.roundWins;
      agentStatsMap[game.agentId].roundLosses += game.roundLosses;
      agentStatsMap[game.agentId].totalRounds += game.roundWins;
      agentStatsMap[game.agentId].totalRounds += game.roundLosses;
      agentStatsMap[game.agentId].totalRAR += ((game.roundWins + game.roundLosses) * rarValueToUse);
      agentStatsMap[game.agentId].totalKastPlusMinus += ((game.roundWins + game.roundLosses) * game.kastPlusMinus);
      agentStatsMap[game.agentId].totalFk += game.fk;
      agentStatsMap[game.agentId].totalUfd += game.ufd;
    }

    const sortedAgentStatsMap = Object.values(agentStatsMap).sort((a, b) => ((b.totalRAR / b.totalRounds) - (a.totalRAR / a.totalRounds)));
    if (!this.playerProfileDataMap[playerId].agentStats) {
      this.playerProfileDataMap[playerId].agentStats = {};
    }
    this.playerProfileDataMap[playerId].agentStats[sinceDate] = sortedAgentStatsMap;
  })

  constructStatsBySplit = action((playerId) => {
    const statsBySplitMap = {};
    for (let game of this.playerProfileDataMap[playerId].matchHistory) {
      if (!statsBySplitMap[game.tournamentId]) {
        statsBySplitMap[game.tournamentId] = {
          tournamentId: game.tournamentId,
          tournamentName: `${game.league}: ${game.tournament}`,
          seriesDate: game.seriesDate,
          gameWins: 0,
          gameLosses: 0,
          roundWins: 0,
          roundLosses: 0,
          totalRounds: 0,
          totalRAR: 0,
          totalKastPlusMinus: 0,
          totalFk: 0,
          totalUfd: 0
        };
      }

      if (statsBySplitMap[game.tournamentId].roundWins > statsBySplitMap[game.tournamentId].roundLosses) {
        statsBySplitMap[game.tournamentId].gameWins += 1;
      } else {
        statsBySplitMap[game.tournamentId].gameLosses += 1;
      }

      const rarValueToUse = game.roundsAboveReplacement !== null ? game.roundsAboveReplacement : game.agnosticRar;

      statsBySplitMap[game.tournamentId].roundWins += game.roundWins;
      statsBySplitMap[game.tournamentId].roundLosses += game.roundLosses;
      statsBySplitMap[game.tournamentId].totalRounds += game.roundWins;
      statsBySplitMap[game.tournamentId].totalRounds += game.roundLosses;
      statsBySplitMap[game.tournamentId].totalRAR += ((game.roundWins + game.roundLosses) * rarValueToUse);
      statsBySplitMap[game.tournamentId].totalKastPlusMinus += ((game.roundWins + game.roundLosses) * game.kastPlusMinus);
      statsBySplitMap[game.tournamentId].totalFk += game.fk;
      statsBySplitMap[game.tournamentId].totalUfd += game.ufd;
    }

    const sortedStatsBySplitMap = Object.values(statsBySplitMap).sort((a, b) => (b.seriesDate > a.seriesDate));
    this.playerProfileDataMap[playerId].statsBySplit = sortedStatsBySplitMap;
  })

  setPlayerProfileInView = action((playerId) => {
    this.playerProfileInView.set(playerId);
  })

  editPlayerModalShowing = observable.box(false);
  playerEditBeingWorkedOn = observable.box(false);

  openCloseEditPlayerModal = action(async () => {
    const currentShowing = this.editPlayerModalShowing.get();
    this.editPlayerModalShowing.set(!currentShowing);
  })

  applyPlayerEdit = action(async (playerId, editedPlayerInfo) => {
    this.playerEditBeingWorkedOn.set(true);
    await API.editPlayer({ playerId, editedPlayerInfo });
    this.playerEditBeingWorkedOn.set(false);
  })

  /**
   * Upcoming, In-Progress, and Recent
   */
  homePageMatchesTabInView = observable.box('upcoming');

  setHomePageMatchesTabInView = action(async (tab) => {
    this.homePageMatchesTabInView.set(tab);
  })

  upcomingMatchesMap = observable.map({});
  allUpcomingMatchesFetched = observable.box(false);
  upcomingMatchesFetching = observable.box(false);

  fetchUpcomingMatches = action(async (props) => {
    if (!this.upcomingMatchesFetching.get()) {
      this.upcomingMatchesFetching.set(true);
      const results = await API.getUpcomingMatches(props);

      this.upcomingMatchesMap[props.id ? props.id : 'all'] = results.body;
      this.upcomingMatchesFetching.set(false);
      if (!props.id) {
        this.allUpcomingMatchesFetched.set(true);
      }
    }
  })

  inProgressMatchesMap = observable.map({});
  allInProgressMatchesFetched = observable.box(false);
  inProgressMatchesFetching = observable.box(false);

  fetchInProgressMatches = action(async (props) => {
    if (!this.inProgressMatchesFetching.get(props)) {
      this.inProgressMatchesFetching.set(true);
      const results = await API.getInProgressMatches();

      this.inProgressMatchesMap[props.id ? props.id : 'all'] = results.body;
      this.inProgressMatchesFetching.set(false);
      if (!props.id) {
        this.allInProgressMatchesFetched.set(true);
      }
    }
  })

  recentMatchesMap = observable.map({});
  allRecentMatchesFetched = observable.box(false);
  recentMatchesFetching = observable.box(false);

  fetchRecentMatches = action(async (props) => {
    if (!this.recentMatchesFetching.get()) {
      this.recentMatchesFetching.set(true);
      const results = await API.getRecentMatches(props);

      this.recentMatchesMap[props.id ? props.id : 'all'] = results.body;
      this.recentMatchesFetching.set(false);
      if (!props.id) {
        this.allRecentMatchesFetched.set(true);
      }
    }
  })

  /**
   * Tournament info
   */
  tournamentsMap = observable.map({});
  tournamentInfoFetching = observable.box(false);

  fetchTournamentInfo = action(async (id) => {
    if (!this.tournamentInfoFetching.get()) {
      this.tournamentInfoFetching.set(true);
      const tournamentInfo = await API.getTournamentInfo(id);
      this.tournamentsMap[id] = tournamentInfo.body[0];
      this.tournamentInfoFetching.set(false);
    }
  })

  /**
   * Quick access
   */
  quickAccessTournaments = observable([]);
  quickAccessFetching = observable.box(false);
  quickAccessFetched = observable.box(false);

  fetchQuickAccess = action(async () => {
    if (!this.quickAccessFetching.get()) {
      this.quickAccessFetching.set(true);
      const response = await API.getQuickAccessTournaments();
      this.quickAccessTournaments = response.body;
      this.quickAccessFetching.set(false);
      this.quickAccessFetched.set(true);
    }
  })

  /**
   * Stats navigation
   */
  statsTournaments = observable([]);
  statsTournamentsFiltered = observable([]);
  isStatsTournamentsFetching = observable.box(false);
  statsTournamentsFiltering = observable.box(false);
  statsTournamentsFilters = observable.object({
    /**
     * It's important to have some pre-seeded keys here,
     * because otherwise the first time a filter is created in this object,
     * Mobx is treating it as a proxy rather than a simple JS object,
     * and that messes stuff up.
     */
    tier: { tier: 'any', label: 'Any Tier', id: 'any' },
    // league
    region: { region: 'any', label: 'All Regions', id: 'any' }
  });

  fetchStatsTournaments = action(async () => {
    const tournaments = await API.getStatsTournaments();
    this.statsTournaments.replace(tournaments.body);
    this.filterStatsTournaments();
  })

  setStatsTournamentsFilterValue(filter, value) {
    this.statsTournamentsFilters[filter] = value;
  }

  filterStatsTournaments = action(async () => {
    this.statsTournamentsFiltering.set(true);
    var filtered = [];

    const tournaments = this.statsTournaments;

    if (!tournaments.length) {
      this.statsTournamentsFiltered.replace([]);
    } else {
      for (let tournament of tournaments) {
        if (
          (
            this.statsTournamentsFilters.region?.region === tournament.regionName ||
            this.statsTournamentsFilters.region?.id === 'any' ||
            !this.statsTournamentsFilters.region
          ) && (
            (this.statsTournamentsFilters.tier?.tier === 'tier2Plus' && ['global', 'challengers'].includes(tournament.tier)) ||
            this.statsTournamentsFilters.tier?.tier === tournament.tier ||
            this.statsTournamentsFilters.tier?.id === 'any'
          )
        ) {
          filtered.push(tournament);
        }
      }
    }
    this.statsTournamentsFiltered.replace(filtered);
    // await this.sortStatsTournaments();

    this.statsTournamentsFiltering.set(false);
  })

  /* sortStatsTournaments = action(async () => {
    const sortOrder = -1;

    let tournamentsCopy = [...toJS(this.statsTournamentsFiltered)];
    tournamentsCopy.sort((rowA, rowB) => {
      let comparison = 0;
      if (rowA[sortColumn] === null || rowA[sortColumn] === "-") {
        return 1;
      }
      if (rowB[sortColumn] === null || rowB[sortColumn] === "-") {
        return -1;
      }

      return comparison * sortOrder * (invertSort ? -1 : 1);
    });
    this.statsTournamentsFiltered.replace(tournamentsCopy);
  }); */

  /**
   * Stats and Player Rankings
   */
  isStatsFetching = observable.box(false);
  isStatsSorting = observable.box(false);
  statsViewsFetched = observable({});
  statsCurrentView = observable.box('');
  statsTournamentInViewId = observable.box(null);
  statsColumns = observable([]);
  statsInView = observable([]);
  filteredStatsContents = observable([]);

  setStatsTournamentInView = action(async (id) => {
    this.statsTournamentInViewId.set(id);
  })

  fetchStats = action(async (statsView) => {
    if (!this.isStatsFetching.get()) {
      this.isStatsFetching.set(true);
      const statsViewString = JSON.stringify(statsView);

      var response;
      switch (statsView.type) {
        case 'players':
          response = await API.getTournamentPlayerStats(statsView);
          break;
        case 'teams':
          response = await API.getTournamentTeamStats(statsView);
          break;
        default:
          console.log(`Nonvalid stats type '${statsView.type}' requested`);
          return;
      }
      if (response.status === 200) {
        this.statsViewsFetched[statsViewString] = response.body;
        this.setStatsInView('stats', this.statsViewsFetched[statsViewString]);
      } else {
        console.log('Unable to fetch stats contents');
      }
      this.isStatsFetching.set(false);
    }
  });

  setStatsInView = action(async (view, stats) => {
    this.statsInView.replace(stats);
    if (stats.length === 0) {
      await this.filterStats(view);
      return;
    }

    if (view === 'stats') {
      const columnsToIgnore = [
        'playerId',
        'teamId',
        'teamTag'
      ];

      // Filter out columns we don't want to display
      const rawColumnList = Object.keys(this.statsInView[0]);
      const columnsToDisplay = rawColumnList.filter(column => !columnsToIgnore.includes(column));

      this.statsColumns.replace(columnsToDisplay);
      this.setTeamFilterOptions();

      await this.filterStats(view);
    }

    if (view === 'playerRankings') {
      const columnsToIgnore = [
        'playerId',
        'teamId',
        'year',
        'tier',
        'notes',
        'target',
        'ignorePlayer'
      ];

      // Filter out columns we don't want to display
      const rawColumnList = Object.keys(this.statsInView[0]);
      const columnsToDisplay = rawColumnList.filter(column => !columnsToIgnore.includes(column));

      this.statsColumns.replace(columnsToDisplay);

      await this.filterStats(view);
    }
  })

  playerRankingsFetched = observable([]);
  playerRankingsContentsAllYears = observable({});
  playerRankingsPlayerStatuses = observable.map({});
  playerStatusIsUpdating = observable.box(false);

  fetchPlayerRankings = action(async (year) => {
    this.isStatsFetching.set(true);

    const response = await API.fetchPlayerRankings(year);
    if (response.status === 200) {
      this.playerRankingsContentsAllYears[year] = response.body;
      this.setStatsInView('playerRankings', response.body);

      for (let player of response.body) {
        this.playerRankingsPlayerStatuses[player.playerId] = {
          'target': player.target,
          'ignorePlayer': player.ignorePlayer
        };
      }

      this.setTeamFilterOptions();
      this.setPlayerRankingsFAYearOptions();
    } else {
      console.log('Unable to fetch Player Rankings contents');
    }
    this.playerRankingsFetched.push(year);
    this.isStatsFetching.set(false);
  });

  statsSortColumn = observable.box('RAR');
  statsSortOrder = observable.box(-1);
  statsFiltersChanged = observable.box(false);
  statsFiltering = observable.box(false);
  statsFilters = observable.object({
    /**
     * It's important to have some pre-seeded keys here,
     * because otherwise the first time a filter is created in this object,
     * Mobx is treating it as a proxy rather than a simple JS object,
     * and that messes stuff up.
     */
    year: { year: new Date().getFullYear(), label: new Date().getFullYear(), id: new Date().getFullYear() },
    contractEnd: { year: 'Any Year', label: 'FA Any Year', id: 'any' },
    tier: { tier: 'tier2Plus', label: 'VCT+Chall', id: 'tier2Plus' },
    iglFilterActive: false,
    minRounds: { rounds: 0, label: '1+ Rds', id: '1+ Rds' },
    minRoundsPlayerRankings: { rounds: 100, label: '100+ Rds', id: '100+ Rds' },
    map: {id: null, 'name': 'All Maps'},
    agent: {id: null, 'name': 'All Agents'}
  });
  statsFiltersTeamOptions = observable([]);
  statsFiltersMapOptions = observable([]);
  statsFiltersMapOptionsFetching = observable.box(false);
  statsFiltersAgentOptions = observable([]);
  statsFiltersAgentOptionsFetching = observable.box(false);
  playerRankingsFAYearOptions = observable([]);
  statsFiltersNeedReset = observable.box(false);

  setStatsFiltersMapOptionsFetching = action((value) => {
    this.statsFiltersMapOptionsFetching.set(value);
  })

  setStatsFiltersAgentOptionsFetching = action((value) => {
    this.statsFiltersAgentOptionsFetching.set(value);
  })

  setSortColumn = action((table, column) => {
    switch (table) {
      case 'playerRankings':
        this.statsSortColumn.set(column);
        break;
      case 'stats':
        this.statsSortColumn.set(column);
        break;
      default:
        console.log('No table specified for setSortColumn')
    }
  });

  setSortOrder = action((table, order) => {
    switch (table) {
      case 'playerRankings':
        this.statsSortOrder.set(order);
        break;
      case 'stats':
        this.statsSortOrder.set(order);
        break;
      default:
        console.log('No table specified for setSortOrder')
    }
  });

  sortStats = action(async () => {
    this.isStatsSorting.set(true);
    let sortColumn = this.statsSortColumn.get();
    let sortOrder = this.statsSortOrder.get();
    let invertSort = false;

    if (sortOrder === 0) {
      sortOrder = -1;
    }

    debug(`Sorting statsInView by ${sortColumn} in sortOrder ${sortOrder === 1 ? "asc" : "desc"}`);

    let statsCopy = [...toJS(this.filteredStatsContents)];
    statsCopy.sort((rowA, rowB) => {
      let comparison = 0;
      if (rowA[sortColumn] === null || rowA[sortColumn] === "-") {
        return 1;
      }
      if (rowB[sortColumn] === null || rowB[sortColumn] === "-") {
        return -1;
      }

      /* Uppercase strings for sorting */
      let rowAValue =
        typeof rowA[sortColumn] === "string"
          ? rowA[sortColumn].toUpperCase()
          : rowA[sortColumn];
      let rowBValue =
        typeof rowB[sortColumn] === "string"
          ? rowB[sortColumn].toUpperCase()
          : rowB[sortColumn];

      /* Convert percentages to floats for sorting */
      if (
        typeof rowA[sortColumn] === "string" &&
        rowA[sortColumn].slice(-1) === "%"
      ) {
        rowAValue = parseFloat(rowAValue.slice(0, -1));
        rowBValue = parseFloat(rowBValue.slice(0, -1));
      }

      if (
        typeof rowA[sortColumn] === "string" &&
        rowA[sortColumn].slice(-1) !== "%"
      ) {
        invertSort = true;
      }

      if (rowAValue > rowBValue) {
        comparison = 1;
      } else if (rowAValue < rowBValue) {
        comparison = -1;
      }
      return comparison * sortOrder * (invertSort ? -1 : 1);
    });
    this.filteredStatsContents.replace(statsCopy);
    this.isStatsSorting.set(false);
  });

  setTeamFilterOptions = action(async () => {
    this.statsFiltersTeamOptions.replace(
      [...new Set(this.statsInView.map((player) => player.Team))].sort((a, b) =>
        a?.localeCompare(b, "en", { sensitivity: "base" })
      )
    );
  });

  setMapFilterOptions = action(async () => {
    this.setStatsFiltersMapOptionsFetching(true);
    const response = await API.getMaps();
    const maps = response.body;
    maps.unshift({id: null, 'name': 'All Maps'})
    this.statsFiltersMapOptions.replace(maps);
    this.setStatsFiltersMapOptionsFetching(false);
  });

  setAgentFilterOptions = action(async () => {
    this.setStatsFiltersAgentOptionsFetching(true);
    const response = await API.getAgents();
    const agents = response.body;
    agents.unshift({id: null, 'name': 'All Agents'})
    this.statsFiltersAgentOptions.replace(agents);
    this.setStatsFiltersAgentOptionsFetching(false);
  });

  setPlayerRankingsFAYearOptions = action(async () => {
    const availableYears = [...new Set(this.statsInView.map((player) => player['FA Year']))].sort();
    const optionsList = availableYears.filter(Boolean).map(year => { return { year: year, label: `FA ${year}`, id: year } });
    optionsList.unshift({ year: 'Any Year', label: 'FA Any Year', id: 'any' });
    this.playerRankingsFAYearOptions.replace(optionsList);
  });

  setStatsFiltersChanged(value) {
    this.statsFiltersChanged.set(value);
  }

  setStatsFilterValue(filter, value) {
    this.statsFilters[filter] = value;
  }

  togglePlayerRankingsTargetFilter = action(async () => {
    this.statsFilters.targets = !this.statsFilters.targets;
    this.filterStats('playerRankings');
  });

  togglePlayerRankingsIglFilter = action(async () => {
    this.statsFilters.iglFilterActive = !this.statsFilters.iglFilterActive;
    this.filterStats('playerRankings');
  });

  resetPlayerRankingsIglAndTargetFilters = action(async () => {
    this.statsFilters.iglFilterActive = false;
    this.statsFilters.targets = false;
  })

  filterStats = action(async (viewType) => {
    this.statsFiltering.set(true);
    var filtered = [];

    let year = this.statsFilters.year.year;
    let currentView = this.statsCurrentView.get();
    let statsInView;

    if (viewType === 'playerRankings') {
      statsInView = this.playerRankingsContentsAllYears[year];
    } else if (viewType === 'stats') {
      statsInView = this.statsViewsFetched[currentView];
    }

    if (Object.keys(this.statsFilters).length > 0) {
      if (!statsInView) {
        this.filteredStatsContents.replace([]);
      } else {
        for (let player of statsInView) {
          if (
            (
              this.statsFilters.regions?.includes(player.Region) ||
              this.statsFilters.regions?.length === 0 ||
              !this.statsFilters.regions
            ) && (
              this.statsFilters.teams?.includes(player.Team) ||
              this.statsFilters.teams?.length === 0 ||
              !this.statsFilters.teams
            ) && (
              this.statsFilters.roles?.includes(player.Role) ||
              this.statsFilters.roles?.length === 0 ||
              !this.statsFilters.roles
            ) && (
              (this.statsFilters.iglFilterActive && player['IGL'] === 'Yes') ||
              !this.statsFilters.iglFilterActive
            ) && (
              (player['FA Year'] && this.statsFilters.contractEnd?.year >= player['FA Year']) ||
              this.statsFilters.contractEnd?.id === 'any' ||
              !player['FA Year'] ||
              viewType === 'stats'
            ) && (
              (this.statsFilters.tier?.tier === 'tier2Plus' && ['global', 'challengers'].includes(player.tier)) ||
              this.statsFilters.tier?.tier === player.tier ||
              this.statsFilters.tier?.id === 'any' ||
              viewType === 'stats'
            ) && (
              (this.statsFilters.minRounds?.rounds <= player.Rds)
            )
          ) {
            filtered.push(player);
          }
        }
      }
      this.filteredStatsContents.replace(filtered);
      await this.sortStats();
    } else {
      this.filteredStatsContents.replace(statsInView);
      await this.sortStats();
    }
    this.statsFiltering.set(false);
    this.statsFiltersChanged.set(false);
  });

  setStatsFiltersNeedReset = action(async (value) => {
    this.statsFiltersNeedReset.set(value);
  })

  setStatsCurrentView = action(async (statsViewString) => {
    this.statsCurrentView.set(statsViewString);
  })

  setColumnsInView = action(async (table) => {
    if (table === 'playerRankings') {
      if (this.filteredPlayerRankingsContents.length > 0) {
        var playerRankingsColumns = Object.keys(this.filteredPlayerRankingsContents[0]);
        this.playerRankingsColumns.replace(playerRankingsColumns);
      } else {
        this.playerRankingsColumns.replace([])
      }
    } else {
      // @TODO
    }
  });

  setPlayerRankingsPlayerStatus = action(async ({ playerId, offseasonYear, statusType, booleanStatus = false, newValue = null }) => {
    this.playerStatusIsUpdating.set(playerId);
    const validStatusTypes = [
      'target',
      'ignorePlayer'
    ];
    if (!validStatusTypes.includes(statusType)) {
      this.playerStatusIsUpdating.set(false);
      const error = {
        code: 400,
        message: `${statusType} is not a valid Player Rankings player status type`
      };
      throw error;
    }

    var statusValue = newValue;
    if (booleanStatus) {
      statusValue = !this.playerRankingsPlayerStatuses[playerId][statusType];
    }

    // Make an API call to post the new status
    const responseCode = await API.updatePlayerRankingsPlayerInfo({
      playerId: playerId,
      offseasonYear: offseasonYear,
      updateField: statusType,
      updateValue: statusValue
    })

    if (responseCode === 200) {
      this.playerRankingsPlayerStatuses[playerId][statusType] = statusValue;
    } else {
      console.error(`Unable to set status on player ID ${playerId}: response code ${responseCode}`)
    }

    this.playerStatusIsUpdating.set(false);
  })

  /**
   * General stats
   */
  statsDefinitionsMap = observable.map({});
  statsDefinitionsAreFetching = observable.box(false);
  statsDefinitionsAreFetched = observable.box(false);

  getStatsDefinitions = action(async () => {
    this.statsDefinitionsAreFetching.set(true);
    const response = await API.getStatsDefinitions();
    if (response.status === 200) {
      for (let stat of response.body) {
        this.statsDefinitionsMap.set(stat.label, stat.definition);
      }
    } else {
      console.log("Could not fetch stats definitions");
    }
    this.statsDefinitionsAreFetched.set(true);
    this.statsDefinitionsAreFetching.set(false);
  });

  /**
   * Player Rankings modal
   */
  playerRankingsModalPlayerId = observable.box();
  playerRankingsModalShowing = observable.box(false);
  playerRankingsModalTab = observable.box('stats');

  openClosePlayerRankingsModal = action(async (playerId) => {
    const currentShowing = this.playerRankingsModalShowing.get();

    if (this.playerRankingsModalPlayerId.get() === playerId) {
      this.playerRankingsModalShowing.set(!currentShowing);
    } else {
      this.playerRankingsModalPlayerId.set(playerId);
      this.playerRankingsModalShowing.set(true);
    }
  })

  setPlayerRankingsModalTab = action(async (tab) => {
    this.playerRankingsModalTab.set(tab)
  })

  /**
   * Notes
   */
  addNoteModalShowing = observable.box(false);
  notesAreBeingWorkedOn = observable.box(false);

  openCloseAddNoteModal = action(async () => {
    const currentShowing = this.addNoteModalShowing.get();
    this.addNoteModalShowing.set(!currentShowing);
  })

  submitNewNote = action(async (entityId, entityType, noteObject) => {
    this.notesAreBeingWorkedOn.set(true);
    await API.addNote({ entityId, entityType, noteObject });

    if (entityType === 'player') {
      const playerProfileData = await API.getPlayerProfile(entityId);
      this.addPlayerNotes(entityId, playerProfileData.notes);
      this.setPlayerProfileInView(entityId);
    }
    // Do same for team, once built

    this.notesAreBeingWorkedOn.set(false);
  })

  deleteNote = action(async (entityId, entityType, noteUuid) => {
    this.notesAreBeingWorkedOn.set(true);
    await API.deleteNote({ entityId, entityType, noteUuid });

    if (entityType === 'player') {
      const playerProfileData = await API.getPlayerProfile(entityId);
      this.addPlayerNotes(entityId, playerProfileData.notes);
      this.setPlayerProfileInView(entityId);
    }
    // Do same for team, once built

    this.notesAreBeingWorkedOn.set(false);
  })

  /**
   * Scrims
   */
  isScrimHistoryFetching = observable.box(false);
  isScrimHistoryFetched = observable.box(false);
  scrimHistory = observable.array([]);

  addScrimModalShowing = observable.box(false);
  scrimBeingAdded = observable.box(false);

  openCloseAddScrimModal = action(async () => {
    const currentShowing = this.addScrimModalShowing.get();
    this.addScrimModalShowing.set(!currentShowing);
  })

  setScrimBeingAdded = action(state => {
    this.scrimBeingAdded.set(state);
  })

  fetchScrimHistory = action(async () => {
    this.isScrimHistoryFetching.set(true);

    const response = await API.fetchScrimHistory();
    if (response.status === 200) {
      this.scrimHistory.replace(response.body);
    } else {
      console.log('Unable to fetch scrim history');
    }

    this.isScrimHistoryFetched.set(true);
    this.isScrimHistoryFetching.set(false);
  })
}

const store = new mainStore();

export default store;
